import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from "@/composables/useNotifications";
import i18n from '@/libs/i18n';
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'

export default function useValueList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError } = useCommon()
  const { updateItem, getItems } = useLambda()
  const userData = store.state?.userStore?.userData
  const clientId = userData.role !== "admin" ? userData.client.$oid : null
  const values = ref([])
  const collection = 'value'

  const tableColumns = computed(() => {
    return [
      { key: 'name', label: i18n.t('message.tableHeader.values'), sortable: true },
      { key: 'Actions', label: i18n.t('message.tableHeader.actions') },
    ]
  })

  const totalValues = ref(0)
  const perPage = ref(10)
  const currentPage = ref(1)
  const perPageOptions = [5, 10, 15, 20, 25, 30, 40, 50, 100]
  const searchQuery = ref('')
  const defaultSortBy = 'name'
  const sortBy = ref(defaultSortBy)
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)

  const fetchValues = async (searchValue = '') => {
    isLoading.value = true

    try {
      const query = {
        client_id: oid(clientId),
        deleted: { $ne: true },
      }

      const items = await getItems({ collection, query })
      if (!items) return

      if (searchValue && searchValue !== searchQuery.value) return

      values.value = searchQuery.value ? items.filter(e => e.name?.toLowerCase().includes(searchQuery.value.toLowerCase())) : items
      totalValues.value = values.value.length

    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.value_list_fetch_error') })
    } finally {
      isLoading.value = false
    }
  }

  const searchValue = searchVal => {
    isLoading.value = true
    fetchValues(searchVal)
  }

  const deleteValue = async (id) => {
    try {
      const query = { _id: oid(id) }
      const action = { $set: { deleted: true } }

      await updateItem({ collection, query, action })

      fetchValues()
      showSuccessMessage(i18n.t('message.value_remove'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.something_went_wrong'))
    }
  }

  const showingMessage = computed(() => {
    const from = ((currentPage.value * perPage.value) - perPage.value) + (values.value.length ? 1 : 0)
    const to = values.value.length + ((currentPage.value * perPage.value) - perPage.value)
    return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalValues.value })
  })

  watch([currentPage, perPage], () => {
    fetchValues()
  })

  watch([searchQuery, sortBy, isSortDirDesc], () => {
    currentPage.value = 1
    fetchValues()
  })

  return {
    fetchValues,
    tableColumns,
    perPage,
    currentPage,
    perPageOptions,
    totalValues,
    showingMessage,
    searchQuery,
    sortBy,
    isSortDirDesc,
    values,
    deleteValue,
    isLoading,
    searchValue,
  }
}
